// Input capture (TECH_SPEC §4.2 / §2.7). Produces sim Actions; the session
// stamps them with the frame that is about to be simulated, so a pointerdown
// takes effect on the very next step — zero added latency (design §2.4).
//
// Two layers on purpose: `GestureRecognizer` is the decision logic (thumb/key
// samples in, Actions out) and knows nothing about the DOM, so gesture feel is
// testable without a browser; `InputController` is the thin adapter that listens
// to real events, calls preventDefault, and gates recording with `setEnabled`.

import { Action } from '@beatme/sim';

/** Vertical travel while held that counts as a slide gesture. */
// 16px (was 24): phone playtests showed the extra 8px of finger travel added
// 1-2 frames of latency to every slide, and slides are the tightest verb.
export const SLIDE_THRESHOLD_PX = 16;

/** A pointer sample, stripped of everything the decision doesn't use. */
export interface PointerSample {
  type: 'down' | 'move' | 'up' | 'cancel';
  /** Distinguishes fingers; extra fingers are ignored while one is down. */
  id?: number;
  /** Horizontal position — carried for completeness; the gesture is vertical
   *  only, which is exactly why a diagonal swipe never slides. */
  x?: number;
  y: number;
}

export interface KeySample {
  type: 'down' | 'up';
  /** `KeyboardEvent.key`. */
  key: string;
  /** `KeyboardEvent.code`, when the layout makes `key` ambiguous. */
  code?: string;
  repeat?: boolean;
}

/** Both spellings on purpose: `key` is the layout-dependent one, `code` the
 *  physical key — a keyboard that reports only one of them still works. */
interface KeyIdentity {
  key: string;
  code?: string;
}

const isSpace = (e: KeyIdentity): boolean => e.key === ' ' || e.code === 'Space';
const isDown = (e: KeyIdentity): boolean => e.key === 'ArrowDown' || e.code === 'ArrowDown';

/**
 * The gesture rules, in one DOM-free place:
 *
 * - pointerdown → `Jump`, immediately (a tap is a jump; there is no tap/hold
 *   disambiguation delay, because latency is the whole feel).
 * - while held, once the finger has travelled `SLIDE_THRESHOLD_PX` *downward*
 *   from its lowest-so-far start → `SlideStart`, once per press.
 * - pointerup/cancel → `SlideEnd`, but only if that press actually slid.
 * - Space → `Jump` (auto-repeat ignored); ArrowDown → `SlideStart` on the first
 *   keydown and `SlideEnd` on keyup.
 *
 * Actions are returned per call; the caller decides what to do with them (the
 * session queues them onto the frame it is about to simulate).
 */
export class GestureRecognizer {
  private pointerId: number | null = null;
  private pointerStartY = 0;
  private pointerMaxY = 0;
  private slideFired = false;
  private keySlideHeld = false;

  /** Forgets any in-flight press — used whenever recording stops or resumes, so
   *  a finger held across a pause cannot leak a stale SlideEnd into the run. */
  reset(): void {
    this.pointerId = null;
    this.slideFired = false;
    this.keySlideHeld = false;
  }

  /** True while a press has already produced its one SlideStart. */
  get sliding(): boolean {
    return this.slideFired;
  }

  pointer(e: PointerSample): Action[] {
    const id = e.id ?? 0;
    if (e.type === 'down') {
      if (this.pointerId !== null) return []; // ignore extra fingers
      this.pointerId = id;
      this.pointerStartY = e.y;
      this.pointerMaxY = e.y;
      this.slideFired = false;
      return [Action.Jump];
    }
    if (this.pointerId !== id) return [];
    if (e.type === 'move') {
      if (e.y > this.pointerMaxY) this.pointerMaxY = e.y;
      if (!this.slideFired && this.pointerMaxY - this.pointerStartY >= SLIDE_THRESHOLD_PX) {
        this.slideFired = true;
        return [Action.SlideStart];
      }
      return [];
    }
    // up / cancel
    this.pointerId = null;
    if (!this.slideFired) return [];
    this.slideFired = false;
    return [Action.SlideEnd];
  }

  key(e: KeySample): Action[] {
    if (e.type === 'down') {
      if (isSpace(e)) {
        if (e.repeat === true) return [];
        return [Action.Jump];
      }
      if (isDown(e)) {
        if (e.repeat === true || this.keySlideHeld) return [];
        this.keySlideHeld = true;
        return [Action.SlideStart];
      }
      return [];
    }
    if (isDown(e) && this.keySlideHeld) {
      this.keySlideHeld = false;
      return [Action.SlideEnd];
    }
    return [];
  }
}

export interface InputOptions {
  /** Fired on the first pointerdown/keydown of the page — unlocks audio. */
  onFirstGesture?: () => void;
}

/** DOM adapter: real events in, drained Actions out. All gesture decisions live
 *  in `GestureRecognizer`; this class only translates and gates. */
export class InputController {
  private readonly recognizer = new GestureRecognizer();
  private pending: Action[] = [];
  private enabled = false;
  private attached = false;
  private firstGestureDone = false;

  constructor(
    private readonly target: HTMLElement,
    private readonly options: InputOptions = {},
  ) {}

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this.target.addEventListener('pointerdown', this.onPointerDown);
    this.target.addEventListener('pointermove', this.onPointerMove);
    this.target.addEventListener('pointerup', this.onPointerUp);
    this.target.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.target.removeEventListener('pointerdown', this.onPointerDown);
    this.target.removeEventListener('pointermove', this.onPointerMove);
    this.target.removeEventListener('pointerup', this.onPointerUp);
    this.target.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }

  /** Only while enabled do gestures turn into recorded sim input. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.reset();
  }

  reset(): void {
    this.pending.length = 0;
    this.recognizer.reset();
  }

  /** Actions accumulated since the previous call, in order. */
  drain(): Action[] {
    if (this.pending.length === 0) return [];
    const out = this.pending;
    this.pending = [];
    return out;
  }

  private queue(actions: Action[]): void {
    if (!this.enabled || actions.length === 0) return;
    for (const a of actions) this.pending.push(a);
  }

  private gesture(): void {
    if (this.firstGestureDone) return;
    this.firstGestureDone = true;
    this.options.onFirstGesture?.();
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    this.gesture();
    const actions = this.recognizer.pointer({ type: 'down', id: e.pointerId, x: e.clientX, y: e.clientY });
    // Suppress the browser's own handling only for a sample we acted on — an
    // extra finger produces nothing and must stay a normal event.
    if (actions.length > 0 && e.cancelable) e.preventDefault();
    this.queue(actions);
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    this.queue(
      this.recognizer.pointer({ type: 'move', id: e.pointerId, x: e.clientX, y: e.clientY }),
    );
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    this.queue(this.recognizer.pointer({ type: 'up', id: e.pointerId, x: e.clientX, y: e.clientY }));
  };

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (!isSpace(e) && !isDown(e)) return;
    this.gesture();
    const actions = this.recognizer.key({
      type: 'down',
      key: e.key,
      code: e.code,
      repeat: e.repeat,
    });
    if (actions.length > 0) e.preventDefault();
    this.queue(actions);
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.queue(this.recognizer.key({ type: 'up', key: e.key, code: e.code }));
  };
}
